import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Select,
  Stack, TextField, Tooltip, Typography, Alert, Collapse,
  List, ListItem, ListItemText, ListItemSecondaryAction, Accordion,
  AccordionSummary, AccordionDetails, Badge
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import useEnvConfig from '../util/useEnvConfig';

const PortfolioGroupManager = ({ accessToken }) => {
  const { rootRestApiEndPoint } = useEnvConfig();

  // Portfolio groups state
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(true);

  // Customer / site browser state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSites, setCustomerSites] = useState(null);
  const [customerFolders, setCustomerFolders] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [siteFilter, setSiteFilter] = useState('');
  const [folderFilter, setFolderFilter] = useState('');

  // OEM types
  const [oemTypes, setOemTypes] = useState([]);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');

  // Add site dialog
  const [addSiteDialogOpen, setAddSiteDialogOpen] = useState(false);
  const [pendingSite, setPendingSite] = useState(null);
  const [pendingOem, setPendingOem] = useState('');
  const [pendingSubTag, setPendingSubTag] = useState('');

  // Error/success
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  // -------------------------------------------------------------------------
  // Load initial data
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!accessToken) return;
    loadGroups();
    loadCustomers();
    loadOemTypes();
  }, [accessToken]);

  const loadGroups = () => {
    setGroupsLoading(true);
    fetch(`${rootRestApiEndPoint}/portfolioGroups`, { headers })
      .then(r => r.json())
      .then(data => { setGroups(data || []); setGroupsLoading(false); })
      .catch(err => { console.error(err); setGroupsLoading(false); });
  };

  const loadGroupDetail = (groupId) => {
    fetch(`${rootRestApiEndPoint}/portfolioGroups/${groupId}`, { headers })
      .then(r => r.json())
      .then(data => setSelectedGroup(data))
      .catch(console.error);
  };

  const loadCustomers = () => {
    fetch(`${rootRestApiEndPoint}/portfolioGroups/customers`, { headers })
      .then(r => r.json())
      .then(data => setCustomers(data || []))
      .catch(console.error);
  };

  const loadOemTypes = () => {
    fetch(`${rootRestApiEndPoint}/portfolioGroups/oemTypes`, { headers })
      .then(r => r.json())
      .then(data => setOemTypes(data || []))
      .catch(console.error);
  };

  // -------------------------------------------------------------------------
  // Customer site browser
  // -------------------------------------------------------------------------
  const handleCustomerChange = (e) => {
    const id = e.target.value;
    setSelectedCustomerId(id);
    setCustomerSites(null);
    setCustomerFolders([]);
    setSiteFilter('');
    setFolderFilter('');
    if (!id) return;

    setSitesLoading(true);
    fetch(`${rootRestApiEndPoint}/portfolioGroups/customers/${id}/sites`, { headers })
      .then(r => r.json())
      .then(data => {
        setCustomerSites(data.sites || []);
        setCustomerFolders(data.folders || []);
        setSitesLoading(false);
      })
      .catch(err => { console.error(err); setSitesLoading(false); });
  };

  const filteredSites = customerSites
    ? customerSites.filter(site => {
        const nameMatch = site.siteName.toLowerCase().includes(siteFilter.toLowerCase());
        const folderMatch = !folderFilter || site.primaryLocation?.some(l => l.id === parseInt(folderFilter));
        return nameMatch && folderMatch;
      })
    : [];

  // Check if a site is already in the selected group
  const isSiteInGroup = (siteId) =>
    selectedGroup?.sites?.some(s => s.SiteId === siteId) || false;

  // -------------------------------------------------------------------------
  // Group CRUD
  // -------------------------------------------------------------------------
  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;
    fetch(`${rootRestApiEndPoint}/portfolioGroups`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ groupName: newGroupName.trim(), description: newGroupDesc.trim() || null }),
    })
      .then(r => r.json())
      .then(() => {
        setCreateDialogOpen(false);
        setNewGroupName('');
        setNewGroupDesc('');
        loadGroups();
        setSuccess('Portfolio group created.');
        setTimeout(() => setSuccess(''), 3000);
      })
      .catch(err => setError(String(err)));
  };

  const handleDeleteGroup = (groupId) => {
    if (!window.confirm('Delete this portfolio group and all its site memberships?')) return;
    fetch(`${rootRestApiEndPoint}/portfolioGroups/${groupId}`, { method: 'DELETE', headers })
      .then(() => {
        if (selectedGroup?.PortfolioGroupId === groupId) setSelectedGroup(null);
        loadGroups();
        setSuccess('Group deleted.');
        setTimeout(() => setSuccess(''), 3000);
      })
      .catch(err => setError(String(err)));
  };

  // -------------------------------------------------------------------------
  // Site membership
  // -------------------------------------------------------------------------
  const handleAddSiteClick = (site) => {
    if (!selectedGroup) {
      setError('Select a portfolio group first.');
      return;
    }
    if (isSiteInGroup(site.siteId)) {
      setError(`${site.siteName} is already in this group.`);
      setTimeout(() => setError(''), 3000);
      return;
    }
    setPendingSite(site);
    setPendingOem('');
    setPendingSubTag('');
    setAddSiteDialogOpen(true);
  };

  const handleConfirmAddSite = () => {
    if (!pendingSite || !selectedGroup) return;

    // Derive sub-portfolio tag from the deepest primaryLocation folder name
    const derivedTag = pendingSubTag ||
      (pendingSite.primaryLocation?.slice(-1)[0]?.name || '');

    fetch(`${rootRestApiEndPoint}/portfolioGroups/${selectedGroup.PortfolioGroupId}/sites`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        siteType:          'PT',
        siteId:            pendingSite.siteId,
        displayName:       pendingSite.siteName,
        subPortfolioTag:   derivedTag || null,
        oemHardwareType:   pendingOem || null,
      }),
    })
      .then(r => r.json())
      .then(() => {
        setAddSiteDialogOpen(false);
        loadGroupDetail(selectedGroup.PortfolioGroupId);
        setSuccess(`${pendingSite.siteName} added.`);
        setTimeout(() => setSuccess(''), 3000);
      })
      .catch(err => setError(String(err)));
  };

  const handleRemoveSite = (siteEntryId, siteName) => {
    if (!window.confirm(`Remove ${siteName} from this group?`)) return;
    fetch(`${rootRestApiEndPoint}/portfolioGroups/sites/${siteEntryId}`, {
      method: 'DELETE',
      headers,
    })
      .then(() => {
        loadGroupDetail(selectedGroup.PortfolioGroupId);
        setSuccess(`${siteName} removed.`);
        setTimeout(() => setSuccess(''), 3000);
      })
      .catch(err => setError(String(err)));
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Box sx={{ p: 2 }}>
      {/* Status messages */}
      <Collapse in={!!error}>
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>
      </Collapse>
      <Collapse in={!!success}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>
      </Collapse>

      <Grid container spacing={2}>
        {/* ── Left column: Group list ── */}
        <Grid item xs={3}>
          <Card>
            <CardHeader
              title="Portfolio Groups"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
              action={
                <Tooltip title="Create new group">
                  <IconButton size="small" onClick={() => setCreateDialogOpen(true)}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent sx={{ p: 0 }}>
              {groupsLoading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No groups yet. Create one to get started.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {groups.map(group => (
                    <ListItem
                      key={group.PortfolioGroupId}
                      button
                      selected={selectedGroup?.PortfolioGroupId === group.PortfolioGroupId}
                      onClick={() => loadGroupDetail(group.PortfolioGroupId)}
                      sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <ListItemText
                        primary={group.GroupName}
                        secondary={`${group.SiteCount ?? ''} sites`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Delete group">
                          <IconButton
                            size="small"
                            onClick={e => { e.stopPropagation(); handleDeleteGroup(group.PortfolioGroupId); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Middle column: Selected group sites ── */}
        <Grid item xs={3}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={selectedGroup ? selectedGroup.GroupName : 'Select a Group'}
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
              subheader={selectedGroup ? `${selectedGroup.sites?.length ?? 0} sites` : ''}
            />
            <CardContent sx={{ p: 0, overflow: 'auto', maxHeight: 600 }}>
              {!selectedGroup ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Select a group from the left to view and manage its sites.
                </Typography>
              ) : selectedGroup.sites?.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No sites yet. Browse customers on the right to add sites.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {selectedGroup.sites?.map(site => (
                    <ListItem
                      key={site.PortfolioGroupSiteId}
                      sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <ListItemText
                        primary={site.SiteName || site.DisplayName}
                        secondary={
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {site.OemHardwareType && (
                              <Chip label={site.OemHardwareType} size="small" variant="outlined" />
                            )}
                            {site.SubPortfolioTag && (
                              <Chip label={site.SubPortfolioTag} size="small" />
                            )}
                          </Stack>
                        }
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove from group">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveSite(site.PortfolioGroupSiteId, site.SiteName || site.DisplayName)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Right column: Customer site browser ── */}
        <Grid item xs={6}>
          <Card>
            <CardHeader
              title="Browse Customer Sites"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
              subheader="Select a customer to browse their sites and add them to a group"
            />
            <CardContent>
              <Stack spacing={2}>
                {/* Customer selector */}
                <FormControl fullWidth size="small">
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={selectedCustomerId}
                    label="Customer"
                    onChange={handleCustomerChange}
                  >
                    <MenuItem value=""><em>Select a customer</em></MenuItem>
                    {customers.map(c => (
                      <MenuItem key={c.CustomerId} value={c.CustomerId}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon fontSize="small" color="action" />
                          <span>{c.CustomerName}</span>
                          <Typography variant="caption" color="text.secondary">
                            (ID: {c.CustomerId})
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {sitesLoading && (
                  <Box sx={{ textAlign: 'center' }}><CircularProgress size={24} /></Box>
                )}

                {customerSites && !sitesLoading && (
                  <>
                    {/* Filters */}
                    <Grid container spacing={1}>
                      <Grid item xs={7}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Filter by site name…"
                          value={siteFilter}
                          onChange={e => setSiteFilter(e.target.value)}
                          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Folder</InputLabel>
                          <Select
                            value={folderFilter}
                            label="Folder"
                            onChange={e => setFolderFilter(e.target.value)}
                          >
                            <MenuItem value="">All folders</MenuItem>
                            {customerFolders.map(f => (
                              <MenuItem key={f.id} value={f.id}>
                                {f.name} ({f.siteCount})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Typography variant="caption" color="text.secondary">
                      {filteredSites.length} of {customerSites.length} sites
                      {!selectedGroup && ' — select a group to add sites'}
                    </Typography>

                    {/* Site list */}
                    <Box sx={{ overflow: 'auto', maxHeight: 480 }}>
                      <List dense disablePadding>
                        {filteredSites.map(site => {
                          const inGroup = isSiteInGroup(site.siteId);
                          const folders = site.primaryLocation?.map(l => l.name).join(' › ') || '';
                          return (
                            <ListItem
                              key={site.siteId}
                              sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: inGroup ? 'action.selected' : 'inherit',
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <span>{site.siteName}</span>
                                    {inGroup && (
                                      <Chip
                                        label="In group"
                                        size="small"
                                        color="success"
                                        icon={<CheckCircleIcon />}
                                      />
                                    )}
                                  </Stack>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    <FolderIcon sx={{ fontSize: 10, mr: 0.3, verticalAlign: 'middle' }} />
                                    {folders}
                                  </Typography>
                                }
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                              <ListItemSecondaryAction>
                                <Tooltip title={inGroup ? 'Already in group' : !selectedGroup ? 'Select a group first' : 'Add to group'}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={inGroup || !selectedGroup}
                                      onClick={() => handleAddSiteClick(site)}
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Create group dialog ── */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Portfolio Group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Group Name"
              fullWidth
              size="small"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Description (optional)"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={newGroupDesc}
              onChange={e => setNewGroupDesc(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add site dialog ── */}
      <Dialog open={addSiteDialogOpen} onClose={() => setAddSiteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Site to Group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>{pendingSite?.siteName}</strong>
              <br />
              <Typography component="span" variant="caption" color="text.secondary">
                Adding to: {selectedGroup?.GroupName}
              </Typography>
            </Typography>
            <Divider />
            <FormControl fullWidth size="small">
              <InputLabel>OEM Hardware Type</InputLabel>
              <Select
                value={pendingOem}
                label="OEM Hardware Type"
                onChange={e => setPendingOem(e.target.value)}
              >
                <MenuItem value=""><em>Not specified</em></MenuItem>
                {oemTypes.map(o => (
                  <MenuItem key={o.OemHardwareType} value={o.OemHardwareType}>
                    {o.DisplayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Sub-Portfolio Tag (optional)"
              fullWidth
              size="small"
              value={pendingSubTag}
              onChange={e => setPendingSubTag(e.target.value)}
              helperText={
                pendingSite?.primaryLocation?.length
                  ? `Suggested: ${pendingSite.primaryLocation.slice(-1)[0]?.name}`
                  : 'e.g. Nexamp MA, DSD Northeast'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSiteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAddSite}>
            Add Site
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortfolioGroupManager;
